import React, { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Sparkles, Shield, Zap, Award } from "lucide-react";
import Navbar from "../components/site/Navbar";
import Footer from "../components/site/Footer";
import { services } from "../data/mock";
import { Button } from "../components/ui/button";
import { useLanguage } from "../i18n/LanguageContext";

const ICONS = [Shield, Award, Zap, Sparkles];

export default function ServiceDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const service = services.find((s) => s.slug === slug);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050818] text-white">
        <div className="text-center">
          <h1 className="font-display text-3xl mb-4">{t("serviceDetail.notFound")}</h1>
          <Button onClick={() => navigate("/")} className="btn-primary border-0 rounded-xl h-11 px-6 font-semibold">{t("serviceDetail.backHome")}</Button>
        </div>
      </div>
    );
  }

  const localized = t(`services.items.${service.slug}`) || {};
  const title = localized.title || service.title;
  const description = localized.description || service.description;
  const features = localized.features || service.features;

  const Icon = service.icon;
  const related = services.filter((s) => s.slug !== service.slug).slice(0, 3);
  const pillars = t("serviceDetail.pillars") || [];

  const titleWords = title.split(" ");
  const accentPart = titleWords.slice(-2).join(" ");
  const headPart = titleWords.slice(0, -2).join(" ");

  return (
    <div className="min-h-screen">
      <Navbar />

      <section className="hero-bg relative overflow-hidden pt-32 pb-20">
        <div className="absolute inset-0 stars" />
        <div className="absolute inset-0 grid-overlay" />
        <div className="orb bg-sky-500/25 w-[500px] h-[500px] top-[-100px] right-[-100px]" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <Link to="/#services" className="inline-flex items-center gap-2 text-sm text-cyan-300 hover:text-cyan-200 mb-8">
            <ArrowLeft className="w-4 h-4" /> {t("serviceDetail.back")}
          </Link>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-3 mb-6">
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${service.accent} shadow-lg shadow-cyan-500/25 flex items-center justify-center`}>
                  <Icon className="w-7 h-7 text-white" strokeWidth={2.2} />
                </div>
                <span className="kicker">{t("serviceDetail.kicker")}</span>
              </div>
              <h1 className="font-display font-bold text-white text-4xl lg:text-6xl leading-[1.1]">
                {headPart} <span className="grad-text">{accentPart}</span>
              </h1>
              <p className="mt-6 text-slate-300 text-lg leading-relaxed">{description}</p>

              <ul className="mt-8 space-y-3">
                {features.map((f) => (
                  <li key={f} className="flex items-center gap-3 text-slate-200">
                    <span className="w-6 h-6 rounded-full bg-cyan-500/15 border border-cyan-400/30 flex items-center justify-center">
                      <Check className="w-3.5 h-3.5 text-cyan-300" strokeWidth={3} />
                    </span>
                    {f}
                  </li>
                ))}
              </ul>

              <div className="mt-10 flex items-center gap-4">
                <Link to="/#contact">
                  <Button className="btn-primary h-12 px-7 rounded-xl font-semibold border-0">
                    {t("serviceDetail.getConsultation")} <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/#case-studies">
                  <Button variant="outline" className="btn-ghost h-12 px-7 rounded-xl font-semibold">
                    {t("serviceDetail.viewCaseStudies")}
                  </Button>
                </Link>
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-sky-500/30 to-cyan-400/20 rounded-3xl blur-2xl" />
              <div className="relative rounded-3xl overflow-hidden glass">
                <img src={service.image} alt={title} className="w-full aspect-[4/3] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050818] via-transparent to-transparent" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-bg relative py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="kicker">{t("serviceDetail.howWeDeliverKicker")}</span>
            <h2 className="mt-5 font-display text-3xl lg:text-4xl font-bold text-white">
              {t("serviceDetail.howWeDeliverTitle1")} <span className="grad-text">{t("serviceDetail.howWeDeliverAccent")}</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {pillars.map((p, i) => {
              const I = ICONS[i] || Sparkles;
              return (
                <div key={p.title} className="glass lift rounded-2xl p-6">
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-sky-500/25 to-cyan-400/25 border border-cyan-400/25 flex items-center justify-center mb-4">
                    <I className="w-5 h-5 text-cyan-300" strokeWidth={2} />
                  </div>
                  <h3 className="font-display text-lg font-semibold text-white">{p.title}</h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{p.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 hero-bg" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <h2 className="font-display text-3xl font-bold text-white mb-8">{t("serviceDetail.related")}</h2>
          <div className="grid md:grid-cols-3 gap-5">
            {related.map((s) => {
              const RI = s.icon;
              const rTitle = t(`services.items.${s.slug}.title`) || s.title;
              const rDesc = t(`services.items.${s.slug}.description`) || s.description;
              return (
                <Link key={s.slug} to={`/services/${s.slug}`} className="group glass lift rounded-2xl p-6">
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${s.accent} flex items-center justify-center mb-4`}>
                    <RI className="w-5 h-5 text-white" strokeWidth={2.2} />
                  </div>
                  <h3 className="font-display text-lg font-semibold text-white group-hover:text-cyan-300 transition">{rTitle}</h3>
                  <p className="mt-2 text-sm text-slate-400 line-clamp-3">{rDesc}</p>
                  <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-cyan-300">
                    {t("serviceDetail.learnMore")} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
